/**
 * 점검 모드 오버레이 — 백엔드 /api/maintenance/status + auth state 기반.
 *
 * 우회 조건 (모두 충족 X, 셋 중 하나면 우회):
 * 1. 로그인 사용자가 admin (user.is_admin === true) — 즉시 client-side 적용
 * 2. 백엔드가 IP 화이트리스트로 bypass=true 반환
 *
 * 비관리자가 /login 으로 들어와 로그인해도 user.is_admin=false 면 오버레이 즉시 표시.
 *
 * 인증 라우트(/login 등)는 점검 중에도 접근 허용 — 어드민이 들어오기 위해.
 * 단 인증 라우트는 노출만 허용하고 로그인 자체는 백엔드/auth 흐름이 처리.
 */
import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { FiRefreshCw, FiCheck, FiMail, FiExternalLink } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { api } from '../../lib/api'
import { useAuthStore } from '../../store/authStore'

/** 점검 모드 중에도 항상 접근 가능 — 어드민 로그인을 위한 인증 라우트. */
const ALWAYS_ALLOWED_PATHS = [
  '/login', '/signup', '/verify-email',
  '/forgot-password', '/reset-password',
  '/oauth/callback', '/auth/callback',  // OAuth 콜백 — JWT 저장 + /me 호출 위해 필수
]

interface MaintenanceStatus {
  active: boolean
  bypass: boolean
  message?: string
  expected_end?: string
  client_ip?: string
}

const POLL_INTERVAL_MS = 60_000

export default function MaintenanceGate({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation()
  const [status, setStatus] = useState<MaintenanceStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const location = useLocation()
  const { user } = useAuthStore()
  const isAuthRoute = ALWAYS_ALLOWED_PATHS.some((p) => location.pathname.startsWith(p))
  const isClientAdmin = !!user?.is_admin

  const check = useCallback(async () => {
    try {
      const { data } = await api.get<MaintenanceStatus>('/maintenance/status', { timeout: 8000 })
      setStatus(data)
    } catch {
      // 백엔드 다운 → fail-open (점검 모드 미적용)
      setStatus({ active: false, bypass: true })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    check()
    const id = setInterval(check, POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [check])

  // 로그인/로그아웃 시 즉시 재확인 — 권한 변화 즉각 반영
  useEffect(() => {
    check()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  // 인증 라우트 — 점검 중에도 항상 접근 가능 (어드민 로그인)
  if (isAuthRoute) return <>{children}</>

  // 어드민으로 식별되면 API 응답 대기 없이 즉시 통과 (auth store persisted 값)
  if (isClientAdmin) {
    const isMaintenanceActive = status?.active === true
    return (
      <>
        {isMaintenanceActive && (
          <div className="fixed top-0 left-0 right-0 z-[8000] bg-amber-500/90 text-zinc-950 text-xs font-semibold py-1.5 px-4 text-center shadow-lg backdrop-blur-sm">
            {t('commonComponents.maintenanceGate.bannerAdminBypass', {
              email: user?.email ?? '',
              defaultValue: '🛠️ 점검 모드 활성 — 일반 사용자는 점검 페이지가 보입니다 (관리자 {{email}} 우회 중)',
            })}
          </div>
        )}
        {children}
      </>
    )
  }

  // 첫 로딩 중 — 점검 페이지 노출 방지 위해 빈 화면
  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-surface-deep">
        <div className="w-6 h-6 rounded-full border-2 border-brand-primary border-t-transparent animate-spin" />
      </div>
    )
  }

  const isMaintenanceActive = status?.active === true
  const canBypass = status?.bypass === true
  const showOverlay = isMaintenanceActive && !canBypass

  return (
    <>
      {/* 관리자 우회 시 상단 배너 */}
      {isMaintenanceActive && canBypass && (
        <div className="fixed top-0 left-0 right-0 z-[8000] bg-amber-500/90 text-zinc-950 text-xs font-semibold py-1.5 px-4 text-center shadow-lg backdrop-blur-sm">
          {isClientAdmin
            ? t('commonComponents.maintenanceGate.bannerAdminBypass', {
                email: user?.email ?? '',
                defaultValue: '🛠️ 점검 모드 활성 — 일반 사용자는 점검 페이지가 보입니다 (관리자 {{email}} 우회 중)',
              })
            : t('commonComponents.maintenanceGate.bannerIpBypass', {
                ip: status?.client_ip ?? '?',
                defaultValue: '🛠️ 점검 모드 활성 — 일반 사용자는 점검 페이지가 보입니다 (IP {{ip}} 우회 중)',
              })}
        </div>
      )}

      {/* 우회 가능하거나 점검 비활성 → 정상 children 렌더 */}
      {!showOverlay && children}

      {/* 비관리자 — 풀스크린 점검 오버레이 (children 미렌더) */}
      <AnimatePresence>
        {showOverlay && (
          <MaintenanceFullscreen status={status} onRetry={check} />
        )}
      </AnimatePresence>
    </>
  )
}

// ─────────────────────────────────────────────────────────────────
// 풀스크린 점검 화면 — "operations status page" 톤 (Stripe·Vercel status)
// ─────────────────────────────────────────────────────────────────
function MaintenanceFullscreen({ status, onRetry }: { status: MaintenanceStatus | null; onRetry: () => void }) {
  const { t, i18n } = useTranslation()
  const [now, setNow] = useState(() => new Date())
  const [nextCheckIn, setNextCheckIn] = useState(60)
  const [retrying, setRetrying] = useState(false)

  // 실시간 시각 (1초 갱신)
  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(tick)
  }, [])

  // 자동 재시도 카운트다운 60→0 → 재시도
  useEffect(() => {
    const tick = setInterval(() => {
      setNextCheckIn((n) => {
        if (n <= 1) {
          onRetry()
          return 60
        }
        return n - 1
      })
    }, 1000)
    return () => clearInterval(tick)
  }, [onRetry])

  const handleManualRetry = () => {
    setRetrying(true)
    onRetry()
    setTimeout(() => setRetrying(false), 800)
  }

  const fmtTime = (d: Date) => d.toLocaleTimeString(i18n.language === 'en' ? 'en-US' : 'ko-KR', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  })
  const fmtDate = (d: Date) => d.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'ko-KR', {
    year: 'numeric', month: 'long', day: 'numeric', weekday: 'short',
  })

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] overflow-y-auto bg-zinc-950"
      role="alertdialog"
      aria-live="assertive"
    >
      {/* 배경 사진 (아주 옅게) */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <img
          src="/assets/hero/landing-hero-ambient.jpg"
          alt=""
          className="w-full h-full object-cover opacity-[0.12]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/60 via-zinc-950/85 to-zinc-950" />
      </div>

      <div className="relative min-h-screen flex flex-col">
        {/* 상단 바: 로고 + 실시간 시각 */}
        <header className="border-b border-white/[0.06]">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
            <span className="text-lg font-semibold text-white tracking-tight" style={{ fontFamily: '"Tinos", "Times New Roman", serif' }}>
              Heinrich
            </span>
            <span className="text-xs font-mono text-zinc-500 tabular-nums">
              {fmtDate(now)} · {fmtTime(now)} KST
            </span>
          </div>
        </header>

        {/* 메인 콘텐츠 */}
        <main className="flex-1 flex items-center justify-center px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full max-w-2xl"
          >
            {/* 상태 라벨 (status page 스타일) */}
            <div className="flex items-center gap-2 mb-6">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
              </span>
              <span className="text-[11px] font-mono uppercase tracking-[0.15em] text-amber-400">
                {t('commonComponents.maintenanceGate.statusLabel', { defaultValue: 'Scheduled Maintenance · In Progress' })}
              </span>
            </div>

            {/* 헤드라인 */}
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4 leading-[1.15]">
              {t('commonComponents.maintenanceGate.title', { defaultValue: '시스템 점검 중입니다' })}
            </h1>
            <p className="text-base text-zinc-300 leading-[1.75] mb-8 max-w-xl">
              {status?.message ?? t('commonComponents.maintenanceGate.defaultMessage', {
                defaultValue: '더 나은 서비스 제공을 위해 점검 작업을 진행하고 있습니다.',
              })}{' '}
              {t('commonComponents.maintenanceGate.resumeNote', {
                defaultValue: '완료되는 대로 서비스를 재개하겠습니다. 작성 중이던 데이터는 안전하게 보존됩니다.',
              })}
            </p>

            {/* 상태 카드 그리드 (4셀, 1px 구분선) */}
            <div className="grid grid-cols-2 gap-px bg-white/[0.08] rounded-md overflow-hidden border border-white/[0.08] mb-6">
              <InfoBlock
                label={t('commonComponents.maintenanceGate.statusCellLabel', { defaultValue: '점검 상태' })}
                value={t('commonComponents.maintenanceGate.inProgress', { defaultValue: '진행 중' })}
                accent
              />
              <InfoBlock
                label={t('commonComponents.maintenanceGate.expectedEndLabel', { defaultValue: '예상 완료' })}
                value={status?.expected_end || t('commonComponents.maintenanceGate.checkingExpected', { defaultValue: '곧 안내' })}
                mono
              />
              <InfoBlock
                label={t('commonComponents.maintenanceGate.nextCheckLabel', { defaultValue: '자동 재확인까지' })}
                value={`${nextCheckIn}s`}
                mono
              />
              <InfoBlock
                label={t('commonComponents.maintenanceGate.dataLabel', { defaultValue: '데이터 안전성' })}
                value={t('commonComponents.maintenanceGate.dataSafe', { defaultValue: '안전 보존' })}
                ok
              />
            </div>

            {/* 진행 중 작업 타임라인 */}
            <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-5 mb-8">
              <div className="text-[11px] font-mono uppercase tracking-[0.14em] text-zinc-500 mb-3">
                {t('commonComponents.maintenanceGate.workLabel', { defaultValue: '진행 중인 작업' })}
              </div>
              <ul className="space-y-2.5">
                <TimelineRow done label={t('commonComponents.maintenanceGate.step1', { defaultValue: '서비스 트래픽 안전 격리' })} />
                <TimelineRow done label={t('commonComponents.maintenanceGate.step2', { defaultValue: '진행 중 작업 안전 종료 및 데이터 백업' })} />
                <TimelineRow active label={t('commonComponents.maintenanceGate.step3', { defaultValue: '시스템 점검 / 업데이트 적용' })} />
                <TimelineRow label={t('commonComponents.maintenanceGate.step4', { defaultValue: '헬스체크 통과 후 서비스 복귀' })} />
              </ul>
            </div>

            {/* 액션 */}
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              <button
                onClick={handleManualRetry}
                disabled={retrying}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-semibold bg-amber-500 hover:bg-amber-400 text-white transition-colors disabled:opacity-60"
              >
                <FiRefreshCw size={14} className={retrying ? 'animate-spin' : ''} />
                {t('commonComponents.maintenanceGate.retry', { defaultValue: '지금 다시 시도' })}
              </button>
              <a
                href="https://blog.naver.com/heinrich_dasan"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-semibold bg-white/[0.04] border border-white/[0.12] text-zinc-200 hover:bg-white/[0.08] hover:border-white/[0.20] transition-colors"
              >
                <FiExternalLink size={13} />
                {t('commonComponents.maintenanceGate.visitBlog', { defaultValue: '하인리히 블로그 보러가기' })}
              </a>
            </div>
          </motion.div>
        </main>

        {/* 하단 푸터 */}
        <footer className="border-t border-white/[0.06] py-5">
          <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-zinc-500">
            <div className="flex items-center gap-2">
              <FiMail size={11} />
              <span>{t('commonComponents.maintenanceGate.contactLabel', { defaultValue: '문의' })}</span>
              <a href="mailto:heinrich_dasan@naver.com" className="font-mono text-zinc-400 hover:text-amber-400 transition-colors">
                heinrich_dasan@naver.com
              </a>
            </div>
            <span className="font-mono">Heinrich · heinrich.dasancms.co.kr</span>
          </div>
        </footer>
      </div>
    </motion.div>
  )
}

function InfoBlock({ label, value, accent, ok, mono }: { label: string; value: string; accent?: boolean; ok?: boolean; mono?: boolean }) {
  return (
    <div className="bg-zinc-950 px-5 py-4">
      <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-zinc-500 mb-1.5">{label}</div>
      <div className={`text-sm font-semibold ${mono ? 'font-mono tabular-nums' : ''} ${accent ? 'text-amber-400' : ok ? 'text-emerald-400' : 'text-white'}`}>
        {value}
      </div>
    </div>
  )
}

function TimelineRow({ label, done, active }: { label: string; done?: boolean; active?: boolean }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-1 shrink-0">
        {done ? (
          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500/20 border border-emerald-500/40">
            <FiCheck size={9} className="text-emerald-400" />
          </span>
        ) : active ? (
          <span className="relative inline-flex h-4 w-4 items-center justify-center">
            <span className="absolute inset-0 rounded-full bg-amber-400/30 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
          </span>
        ) : (
          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-zinc-700" />
        )}
      </span>
      <span className={`text-sm leading-snug ${done ? 'text-zinc-500 line-through decoration-zinc-700' : active ? 'text-white font-medium' : 'text-zinc-500'}`}>
        {label}
      </span>
    </li>
  )
}
